import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../utils/tokenManager";
import logoIcon from "../../assets/common/logo-icon.svg";
import logout from "../../assets/common/logout.svg";
import editIcon from "../../assets/dashboard/edit-icon.svg";
import UploadFileModal from "../common/UploadFileModal";
import DateFilter from "../common/DateFilter";
import DownloadReport from "../common/DownloadReport";
import axios from "axios";
import AdminSidebar from "components/common/AdminSidebar";
import PageSizeFilter from "./adminComponents/PageSizeFilter";
import TableSearch from "./adminComponents/TableSearch";
import TableSortDropdown from "./adminComponents/TableSortDropdown";
import MobileAdminDashboard from "./mobile/MobileAdminDashboard";

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({ start: null, end: null });
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("adminSidebarPinned");
    return saved === "true";
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("name");
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: null,
  });

  const fields = [
    { label: t("admin.option.agent"), value: "agent_name" },
    { label: t("dashboard.table.company"), value: "company_name" },
  ];

  const user = sessionStorage.getItem("userName");
  const clinentId = sessionStorage.getItem("clinentId");

  const fetchAgents = async (startDate, endDate) => {
    setLoading(true);
    setError(null);
    try {
      const accessToken = await getValidToken();
      let start_date, end_date;
      if (startDate && endDate) {
        start_date = startDate.format("YYYY-MM-DD");
        end_date = endDate.format("YYYY-MM-DD");
      } else {
        const today = new Date();
        start_date = `${today.getFullYear()}-${String(
          today.getMonth() + 1,
        ).padStart(2, "0")}-01`;
        end_date = `${today.getFullYear()}-${String(
          today.getMonth() + 1,
        ).padStart(2, "0")}-${new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0,
        ).getDate()}`;
      }
      const config = {
        method: "get",
        url: `${process.env.REACT_APP_APIBASEURL}/api/dashboard/agents`,
        params: {
          start_date: start_date,
          end_date: end_date,
          // Restrict data to the current admin's client when available
          ...(clinentId ? { client_id: clinentId } : {}),
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await axios.request(config);

      let agentsData = response.data.agents || [];

      // As an extra safety layer, filter on the frontend as well
      if (clinentId) {
        agentsData = agentsData.filter(
          (agent) => String(agent.client_id) === String(clinentId),
        );
      }

      setAgents(agentsData);
      setCurrentPage(1);
    } catch (err) {
      setError(t("dashboard.errorFetchingAgents") || "Error fetching agents");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchAgents();
  }, []);
  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange({ start: startDate, end: endDate });
    fetchAgents(startDate, endDate);
  };

  const handleClient = (clientId) => {
    navigate(`/client-confirmation/${clientId}`);
  };

  const highlight = (text = "") => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, "gi");
    const parts = text.toString().split(regex);

    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={i} className="bg-yellow-200 font-semibold">
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  // 1️⃣ Filter first
  const filteredAgents = agents.filter((agent) => {
    if (!searchTerm) return true;

    const fieldValue = agent[searchField];

    if (!fieldValue) return false;

    return fieldValue
      .toString()
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
  });

  // 2️⃣ SORT
  const sortedAgents = [...filteredAgents].sort((a, b) => {
    const { field, direction } = sortConfig;

    if (!field || !direction) return 0;

    let valueA = (a[field] || "").toString().toLowerCase();
    let valueB = (b[field] || "").toString().toLowerCase();

    return direction === "asc"
      ? valueA.localeCompare(valueB)
      : valueB.localeCompare(valueA);
  });

  // 3️⃣ Then paginate sorted data
  const totalPages = Math.ceil(sortedAgents.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = sortedAgents.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  return (
    <>
    <div className="hidden lg:flex min-h-screen bg-secondaryBg">
      <AdminSidebar
        logoIcon={logoIcon}
        logout={logout}
        labelClassName="bg-primary"
        onUploadClick={() => setShowUploadModal(true)}
        onToggle={(pinned) => setSidebarPinned(pinned)}
      />
      <div
        className={`flex-1 flex flex-col px-4 sm:px-8 py-6 md:py-7 mt-10 transition-all duration-300 overflow-visible ${
          sidebarPinned ? "ml-[250px]" : "ml-[80px]"
        }`}
      >
        <div className="max-w-6xl mx-auto w-full">
          <span className="text-lg md:text-xl text-[#0A1E4C] font-medium">
            {t("dashboard.greeting", { name: user })}
          </span>
          <h1 className="text-[30px] font-bold text-[#0A1E4C] mb-6">
            {t("welcome")} Kikonnect
          </h1>

          <div className="relative flex items-center mb-8">
            <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
            <hr className="flex-1 border-t-2 border-[#0A1E4C]" />
            <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
          </div>

          <div className="flex flex-col md:flex-row md:justify-between gap-2 mb-2 w-full">
            <div data-tour-id="date-filter">
              <DateFilter onDateRangeChange={handleDateRangeChange} />
            </div>
            <div className="relative z-50 flex items-center gap-3">
              <TableSearch
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                searchField={searchField}
                setSearchField={setSearchField}
                fields={fields}
                onSearchChange={() => setCurrentPage(1)}
              />

              <PageSizeFilter
                value={itemsPerPage}
                onChange={(size) => {
                  setItemsPerPage(size);
                  setCurrentPage(1);
                }}
              />

              <div data-tour-id="download-report">
                <DownloadReport agents={agents} />
              </div>
            </div>
          </div>

          <div className="mt-5">
            <div className="bg-white rounded-xl shadow-lg p-0 w-[270px] md:w-auto h-auto border border-[#E3E8F0] overflow-x-auto lg:overflow-x-hidden">
              <table className="w-full text-primary">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="py-3 px-2 sm:px-4 rounded-tl-xl">
                      <TableSortDropdown
                        label={t("admin.option.agent")}
                        value={
                          sortConfig.field === "agent_name"
                            ? sortConfig.direction
                            : null
                        }
                        onChange={(value) => {
                          setSortConfig({
                            field: "agent_name",
                            direction: value,
                          });
                          setCurrentPage(1);
                        }}
                        options={[
                          { label: "A-Z", value: "asc" },
                          { label: "Z-A", value: "desc" },
                        ]}
                      />
                    </th>
                    <th className="py-3 px-2 sm:px-4">
                      <TableSortDropdown
                        label={t("dashboard.table.company")}
                        value={
                          sortConfig.field === "company_name"
                            ? sortConfig.direction
                            : null
                        }
                        onChange={(value) => {
                          setSortConfig({
                            field: "company_name",
                            direction: value,
                          });
                          setCurrentPage(1);
                        }}
                        options={[
                          { label: "A-Z", value: "asc" },
                          { label: "Z-A", value: "desc" },
                        ]}
                      />
                    </th>
                    <th className="py-3 px-2 sm:px-4">
                      {t("dashboard.table.totalCalls")}
                    </th>
                    <th className="py-3 px-2 sm:px-4">
                      {t("dashboard.table.answered")}
                    </th>
                    <th className="py-3 px-2 sm:px-4">
                      {t("dashboard.table.leads")}
                    </th>
                    <th className="py-3 px-2 sm:px-4">
                      {t("dashboard.table.meetings")}
                    </th>
                    <th className="py-3 px-2 sm:px-4">
                      {t("dashboard.table.outOfServ")}
                    </th>
                    <th className="py-3 px-2 sm:px-4 rounded-tr-xl">
                      {t("dashboard.table.success")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center py-8">
                        {t("dashboard.loading")}
                      </td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="8" className="text-center text-red-500 py-8">
                        {error}
                      </td>
                    </tr>
                  ) : filteredAgents.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-8">
                        {t("dashboard.noAgents")}
                      </td>
                    </tr>
                  ) : (
                    currentItems.map((agent, idx) => (
                      <tr
                        key={startIndex + idx}
                        className="text-center border-b last:border-b-0"
                      >
                        <td className="py-3 sm:py-5 px-2 sm:px-4 font-bold">
                          {highlight(agent.agent_name)}
                        </td>
                        <td
                          className="py-3 sm:py-5 px-2 sm:px-4 relative"
                          data-tour-id="agents-table"
                        >
                          <div className="flex items-center justify-center gap-2">
                            {highlight(agent.company_name)}
                            <span className="absolute right-0 top-4 w-4 h-4 flex items-center justify-center rounded-full bg-lightBlue cursor-pointer">
                              <img
                                src={editIcon}
                                alt="editIcon"
                                className="w-2 h-2"
                                onClick={() => handleClient(agent.client_id)}
                              />
                            </span>
                          </div>
                        </td>
                        <td>{agent.total_calls_made ?? "-"}</td>
                        <td>{agent.total_calls_answered ?? "-"}</td>
                        <td>{agent.total_leads ?? "-"}</td>
                        <td className="bg-primary text-white">
                          {agent.total_meetings_booked ?? "-"}
                        </td>
                        <td>{agent.total_discarded ?? "-"}</td>
                        <td>
                          {agent.total_meetings_confirmed_attended ?? "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {agents.length > 0 && (
            <div className="flex items-center justify-center space-x-5 text-sm my-4">
              <button
                className={`p-2 rounded border ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400"
                    : "bg-white text-icon"
                }`}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <span className="text-[#000] font-semibold">
                <strong className="bg-white p-1 rounded border">
                  {currentPage}
                </strong>{" "}
                of {totalPages}
              </span>
              <button
                className={`p-2 rounded border ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400"
                    : "bg-white text-icon"
                }`}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      <UploadFileModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
      />
    </div>
    <div className="lg:hidden">
      <MobileAdminDashboard />
    </div>
    </>
  );
};

export default Dashboard;
